import { useId, useState, type ReactNode } from "react";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import type { Promisable } from "type-fest";

import type { MhaStatus } from "@thalamos/aspire-common";
import { defaultPatientListSort } from "@thalamos/aspire-common";

import type { FilterOption } from "./FilterableColumnHeader.js";
import type { PatientListSort } from "./SortableColumnHeader.js";
import { Button } from "~/components/design-system/index.js";

const sortChoices: readonly { label: string; sort: PatientListSort }[] = [
  { label: "Name (A–Z)", sort: { sortBy: "name", sortDirection: "asc" } },
  {
    label: "Date of birth (oldest first)",
    sort: { sortBy: "dateOfBirth", sortDirection: "asc" },
  },
  {
    label: "Expiry (soonest first)",
    sort: { sortBy: "expiry", sortDirection: "asc" },
  },
  { label: "Recently added", sort: defaultPatientListSort },
];

function isSameSort(
  a: Readonly<PatientListSort>,
  b: Readonly<PatientListSort>,
) {
  return a.sortBy === b.sortBy && a.sortDirection === b.sortDirection;
}

function hasSameMembers<T>(a: readonly T[], b: readonly T[]) {
  return a.length === b.length && a.every((value) => b.includes(value));
}

function Section({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="fieldset"
      sx={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}
    >
      <Typography
        component="legend"
        sx={{ fontWeight: 700, padding: 0, marginBottom: 0.5 }}
      >
        {heading}
      </Typography>
      {children}
    </Box>
  );
}

function FilterSection<T extends string>({
  heading,
  options,
  selected,
  onSelectionChange,
}: {
  heading: string;
  options: readonly FilterOption<T>[];
  selected: readonly T[];
  onSelectionChange: (selected: T[]) => Promisable<void>;
}) {
  if (options.length === 0) {
    return null;
  }

  return (
    <Section heading={heading}>
      {options.map((option) => {
        const isSelected = selected.includes(option.value);

        return (
          <FormControlLabel
            key={option.value}
            sx={{ display: "flex" }}
            label={option.label}
            control={
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  void onSelectionChange(
                    isSelected
                      ? selected.filter((value) => value !== option.value)
                      : [...selected, option.value],
                  );
                }}
              />
            }
          />
        );
      })}
    </Section>
  );
}

/**
 * Stages a sort and filters for the patient list and applies them together on
 * Show results, reporting only those that changed; closing discards them. A
 * selected filter value must be among the options, or it is neither shown nor
 * can be unticked.
 */
export function FilterAndSortDialog({
  sort,
  onSortChange,
  locationOptions,
  selectedLocationNames,
  onLocationNamesChange,
  mhaStatusOptions,
  selectedMhaStatuses,
  onMhaStatusesChange,
  onClose,
}: {
  sort: PatientListSort;
  onSortChange: (sort: PatientListSort) => Promisable<void>;
  locationOptions: readonly FilterOption[];
  selectedLocationNames: readonly string[];
  onLocationNamesChange: (locationNames: string[]) => Promisable<void>;
  mhaStatusOptions: readonly FilterOption<MhaStatus>[];
  selectedMhaStatuses: readonly MhaStatus[];
  onMhaStatusesChange: (statuses: MhaStatus[]) => Promisable<void>;
  onClose: () => Promisable<void>;
}) {
  const titleId = useId();
  const [draftSort, setDraftSort] = useState(sort);
  const [draftLocationNames, setDraftLocationNames] = useState<string[]>([
    ...selectedLocationNames,
  ]);
  const [draftMhaStatuses, setDraftMhaStatuses] = useState<MhaStatus[]>([
    ...selectedMhaStatuses,
  ]);

  // Only some sorts are offered here, so the one in force may match none.
  const chosenSortLabel =
    sortChoices.find((choice) => isSameSort(choice.sort, draftSort))?.label ??
    null;

  const appliesChoices = () => {
    void onClose();
    if (!isSameSort(draftSort, sort)) {
      void onSortChange(draftSort);
    }
    if (!hasSameMembers(draftLocationNames, selectedLocationNames)) {
      void onLocationNamesChange(draftLocationNames);
    }
    if (!hasSameMembers(draftMhaStatuses, selectedMhaStatuses)) {
      void onMhaStatusesChange(draftMhaStatuses);
    }
  };

  return (
    <Dialog
      open
      onClose={() => {
        void onClose();
      }}
      fullWidth
      maxWidth="xs"
      scroll="paper"
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            margin: 2,
            width: "calc(100% - 32px)",
            maxHeight: "calc(100% - 32px)",
          },
        },
      }}
    >
      {/* The close button sits beside the title, not in it, so that the
          dialog's name is the title alone. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 1.5,
        }}
      >
        <DialogTitle id={titleId} sx={{ fontWeight: 700 }}>
          Filter and sort
        </DialogTitle>
        <IconButton
          aria-label="Close"
          onClick={() => {
            void onClose();
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      >
        <Section heading="Sort by">
          <RadioGroup
            value={chosenSortLabel}
            onChange={(_event, label) => {
              const choice = sortChoices.find(
                (candidate) => candidate.label === label,
              );
              if (choice !== undefined) {
                setDraftSort(choice.sort);
              }
            }}
          >
            {sortChoices.map((choice) => (
              <FormControlLabel
                key={choice.label}
                value={choice.label}
                label={choice.label}
                control={<Radio />}
              />
            ))}
          </RadioGroup>
        </Section>
        <FilterSection
          heading="Hospital / Place of Safety"
          options={locationOptions}
          selected={draftLocationNames}
          onSelectionChange={setDraftLocationNames}
        />
        <FilterSection
          heading="MHA Status"
          options={mhaStatusOptions}
          selected={draftMhaStatuses}
          onSelectionChange={setDraftMhaStatuses}
        />
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          padding: 2,
          gap: 1.5,
          flexWrap: "wrap",
          "& > *": { flex: "1 1 auto", whiteSpace: "nowrap" },
        }}
      >
        <Button
          label="Clear all"
          outlined
          onClick={() => {
            setDraftSort(defaultPatientListSort);
            setDraftLocationNames([]);
            setDraftMhaStatuses([]);
          }}
        />
        <Button label="Show results" onClick={appliesChoices} />
      </DialogActions>
    </Dialog>
  );
}
